import { DeepPartial } from 'util/typeUtils';
import {
  Dealer,
  DealerWithFlags,
} from '@upstart/harmony/lib/legos/dealerTypes';
import { GetDealerSetupFeatureConfigsResponse } from 'hooks/services';
import { type FormData } from '../types';

export const getV2Payloads = ({
  formData,
  changedData,
}: {
  formData: FormData;
  changedData: DeepPartial<FormData>;
}) => {
  const usernameChanged =
    changedData.featureConfig?.softCreditPull &&
    'username' in changedData.featureConfig.softCreditPull;

  const getSevenHundredCreditPayload = () => {
    if (
      usernameChanged &&
      !changedData.featureConfig?.softCreditPull?.username
    ) {
      return { sevenHundredCredit: null };
    }
    if (
      changedData.featureConfig?.softCreditPull?.username ||
      changedData.featureConfig?.softCreditPull?.password
    ) {
      return {
        sevenHundredCredit: {
          username: formData.featureConfig?.softCreditPull?.username,
          password: formData.featureConfig?.softCreditPull?.password,
        },
      };
    }
    return {};
  };
  const sevenHundredCreditPayload = getSevenHundredCreditPayload();

  const sellerRightToCancelPayload = changedData.featureConfig
    ?.sellerRightToCancel
    ? {
        sellerRightToCancel: {
          days: formData.featureConfig?.sellerRightToCancel?.days ?? null,
          fee: formData.featureConfig?.sellerRightToCancel?.fee ?? null,
        },
      }
    : {};

  const featureConfigs = {
    ...sevenHundredCreditPayload,
    ...sellerRightToCancelPayload,
  };

  return {
    featureConfigs:
      Object.keys(featureConfigs).length > 0 ? featureConfigs : undefined,
  };
};

/**
 * @description Removes v2-only state from a FormData, returning it back into a dealer object
 */
export const omitV2StateFromChanges = (
  formDataChanges: DeepPartial<FormData>,
): DeepPartial<Dealer> => {
  const newDealer: DeepPartial<FormData> = { ...formDataChanges };
  if (formDataChanges.featureConfig?.softCreditPull) {
    const {
      softCreditPull: {
        username,
        password,
        hasExistingPassword,
        ...v1SoftCreditPull
      },
    } = formDataChanges.featureConfig;

    newDealer.featureConfig = {
      ...formDataChanges.featureConfig,
      softCreditPull: v1SoftCreditPull,
    };
  }
  if (newDealer.featureConfig?.sellerRightToCancel) {
    const { sellerRightToCancel, ...restFeatureConfig } =
      newDealer.featureConfig;
    newDealer.featureConfig = restFeatureConfig;
  }
  return newDealer;
};

/**
 * @description combines the legacy `Dealer` object with responses from workflow APIs
 * to construct a FormData.
 * Dealers softCreditPull config has been partially migrated to a new table and API,
 * but react-jsonschema-form wants groups in the UI to follow the forms data structure,
 * which currently matches Dealer. This leaves us with a few options for its UI:
 *  1. Have 700c credentials appear in a standalone group, separate from the other configs
 *  2. Make a specialized widget that acts as a "portal" to some other path in the form data object
 *  3. Construct a formData object out of dealer and v2 state that makes react-jsonschema-form happy
 * This function implements option 3.
 *
 * V2 configs that need to appear in the featureConfig UI section (e.g., sellerRightToCancel)
 * are mixed into `featureConfig` here and stripped by `omitV2StateFromChanges` before saving.
 * Truly standalone v2 configs should be top-level properties on `FormData`.
 */
export const insertV2State = ({
  dealer,
  featureConfigs,
}: {
  dealer: DealerWithFlags;
  featureConfigs: GetDealerSetupFeatureConfigsResponse | undefined;
}): FormData => ({
  ...dealer,
  featureConfig: {
    ...dealer.featureConfig,
    softCreditPull: {
      digitalFinance: false,
      inStore: false,
      inStoreCreditApp: false,
      online: false,
      ...dealer.featureConfig?.softCreditPull,
      username: featureConfigs?.sevenHundredCredit?.username,
      password: '',
      hasExistingPassword: !!featureConfigs?.sevenHundredCredit?.hasPassword,
    },
    sellerRightToCancel: featureConfigs?.sellerRightToCancel
      ? {
          days: featureConfigs.sellerRightToCancel.days ?? undefined,
          fee: featureConfigs.sellerRightToCancel.fee ?? undefined,
        }
      : undefined,
  },
});
